import { useEffect, useState } from "react";
import { AnimatePresence, motion, Variants } from "framer-motion";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronRight,
  Coins,
  CreditCard,
  Leaf,
  LucideIcon,
  Mail,
  MonitorSmartphone,
  PlugZap,
  ShieldCheck,
  Store,
  TrendingDown,
  Zap,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import FooterView from "../components/home/layout/FooterView";

type TargetKey = "지자체" | "금융사" | "브랜드" | "POS사";

const SectionLabel = ({ number, title }: { number: string; title: string }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="w-9 h-9 rounded-full bg-emerald-700 text-white flex items-center justify-center text-base font-bold">
      {number}
    </div>
    <div className="text-base laptop:text-lg font-semibold text-emerald-800 tracking-wide">
      {title}
    </div>
  </div>
);

const Pill = ({
  children,
  active,
}: {
  children: React.ReactNode;
  active: boolean;
}) => (
  <span
    className={`inline-block px-4 py-2 rounded-full text-sm font-semibold border transition ${
      active
        ? "bg-emerald-700 text-white border-emerald-700"
        : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500"
    }`}
  >
    {children}
  </span>
);

const FeatureCard = ({
  icon: Icon,
  title,
  desc,
}: {
  icon: LucideIcon;
  title: string;
  desc: string;
}) => (
  <div className="group rounded-3xl border border-slate-100 bg-white p-6 shadow-sm hover:shadow-xl hover:shadow-emerald-200/40 hover:border-emerald-400 hover:-translate-y-1 transition-all duration-300 cursor-default">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-700 group-hover:text-white group-hover:scale-110 transition-all duration-300">
        <Icon size={24} />
      </div>
      <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-800 transition-colors duration-300">
        {title}
      </h3>
    </div>
    <p className="text-sm leading-6 text-slate-600">{desc}</p>
  </div>
);

const TargetCard = ({
  icon: Icon,
  title,
  bullets,
  active,
}: {
  icon: LucideIcon;
  title: string;
  bullets: string[];
  active: boolean;
}) => (
  <div
    className={`rounded-3xl p-4 tablet:p-6 border shadow-sm transition-colors duration-500 ${
      active
        ? "bg-emerald-800 text-white border-emerald-800"
        : "bg-white text-slate-900 border-slate-100"
    }`}
  >
    <div className="flex tablet:block items-center gap-3 mb-3 tablet:mb-0">
      <div
        className={`w-10 h-10 tablet:w-12 tablet:h-12 rounded-2xl flex items-center justify-center shrink-0 tablet:mb-5 transition-colors duration-500 ${
          active ? "bg-white/15" : "bg-emerald-50 text-emerald-700"
        }`}
      >
        <Icon size={24} />
      </div>
      <h3 className="text-base tablet:text-lg font-bold tablet:mb-4">
        {title}
      </h3>
    </div>
    <ul
      className={`space-y-1.5 tablet:space-y-2 text-[13px] tablet:text-sm transition-colors duration-500 ${
        active ? "text-emerald-50" : "text-slate-600"
      }`}
    >
      {bullets.map((b) => (
        <li key={b} className="flex gap-2">
          <CheckCircle2
            size={16}
            className={`shrink-0 mt-0.5 transition-colors duration-500 ${
              active ? "text-emerald-200" : "text-emerald-600"
            }`}
          />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  </div>
);

const PhoneMockup = () => {
  const barcodeWidths = [
    3, 1, 2, 4, 1, 3, 2, 1, 4, 2, 3, 1, 2, 3, 1, 4, 2, 1, 3, 2, 4, 1, 3, 2, 1,
    4, 2, 3, 1, 2,
  ];
  return (
    <div className="relative mx-auto w-[270px] h-[560px] rounded-[42px] bg-slate-950 p-3 shadow-2xl">
      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-950 rounded-b-2xl z-20" />
      <div className="w-full h-full rounded-[32px] bg-white overflow-hidden flex flex-col">
        {/* Brand bar */}
        <div className="bg-emerald-700 px-5 pt-9 pb-4 text-white shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center">
                <Leaf size={16} className="text-emerald-50" />
              </div>
              <div>
                <div className="text-[9px] opacity-80 tracking-[0.15em] leading-3">
                  GREEN RECEIPT
                </div>
                <div className="text-[13px] font-black leading-tight mt-0.5">
                  발급 완료
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] opacity-80 leading-3">절감 탄소</div>
              <div className="text-base font-black leading-tight mt-0.5">
                10g
              </div>
            </div>
          </div>
        </div>

        {/* Paper */}
        <div className="flex-1 bg-[#fafaf6] px-4 pt-4 pb-3 overflow-hidden">
          {/* Store header */}
          <div className="text-center">
            <div className="text-[14px] font-extrabold tracking-tight text-slate-900">
              OO카페 광명점
            </div>
            <div className="mt-1 text-[9px] text-slate-500 leading-3">
              경기 광명시 새빛로 123
            </div>
            <div className="text-[9px] text-slate-500 leading-3 mt-0.5">
              사업자 123-45-67890 · TEL 02-1234-5678
            </div>
          </div>

          <div className="border-t border-dashed border-slate-300 my-2.5" />

          {/* Meta */}
          <div className="flex justify-between text-[9px] text-slate-500 leading-4">
            <span>2026-05-20 14:30</span>
            <span>POS #01</span>
          </div>
          <div className="flex justify-between text-[9px] text-slate-500 leading-4">
            <span>주문번호</span>
            <span>1023</span>
          </div>

          <div className="border-t border-dashed border-slate-300 my-2.5" />

          {/* Items */}
          <div className="flex justify-between items-baseline">
            <div className="flex items-center gap-1.5">
              <div className="text-[11px] font-semibold text-slate-900">
                아메리카노 (HOT)
              </div>
              <span className="text-[7px] font-black tracking-wider px-1 py-[1px] rounded-sm bg-emerald-700 text-white">
                ECO
              </span>
            </div>
            <div className="text-[11px] font-semibold text-slate-900">
              4,800
            </div>
          </div>
          <div className="text-[9px] text-slate-400 leading-3 mt-0.5">
            단가 4,800 · 수량 1
          </div>

          <div className="flex items-center gap-1 text-[9px] text-slate-500 leading-3 mt-1">
            <CheckCircle2
              size={10}
              className="text-emerald-600 shrink-0"
              strokeWidth={2.5}
            />
            <span>일회용 컵 미사용</span>
          </div>

          <div className="border-t border-dashed border-slate-300 my-2.5" />

          {/* Totals */}
          <div className="space-y-1">
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>공급가액</span>
              <span>4,364</span>
            </div>
            <div className="flex justify-between text-[10px] text-slate-500">
              <span>부가세</span>
              <span>436</span>
            </div>
            <div className="flex justify-between text-[13px] font-extrabold text-slate-900 pt-0.5">
              <span>합계</span>
              <span>4,800원</span>
            </div>
          </div>

          <div className="border-t border-dashed border-slate-300 my-2.5" />

          {/* Card */}
          <div className="text-[9px] text-slate-500 leading-4">
            <div className="flex justify-between">
              <span>신용카드 (KB국민)</span>
              <span>4,800원</span>
            </div>
            <div className="opacity-70 tracking-wider">****-****-****-1234</div>
          </div>

          {/* Carbon point highlight */}
          <div className="mt-2.5 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Leaf size={11} className="text-emerald-700" />
              <span className="text-[10px] font-bold text-emerald-800">
                탄소중립포인트
              </span>
            </div>
            <span className="text-[11px] font-black text-emerald-700">
              +100원
            </span>
          </div>

          {/* Barcode */}
          <div className="mt-3 flex items-end justify-center gap-[1px] h-6">
            {barcodeWidths.map((w, i) => (
              <div
                key={i}
                className="bg-slate-900 h-full"
                style={{ width: `${w}px` }}
              />
            ))}
          </div>
          <div className="text-center text-[8px] text-slate-400 tracking-[0.2em] mt-0.5">
            1023-052026
          </div>
        </div>
      </div>
    </div>
  );
};

const DigitalReceiptPage = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();

  const didShowInquirePage = () => {
    navigate("/contact", { state: { type: "receipt" } });
  };

  const didShowIntroducePage = () => {
    navigate("/");
  };

  type PeriodMap = Record<number, boolean>;
  const [expandedPeriods, setExpandedPeriods] = useState<PeriodMap>({});
  const [target, setTarget] = useState<TargetKey>("지자체");

  const togglePeriod = (periodIdx: number) => {
    setExpandedPeriods((prev) => ({
      ...prev,
      [periodIdx]: !prev[periodIdx],
    }));
  };

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash]);

  // Auto-rotate active target card every 3 seconds
  useEffect(() => {
    const targetKeys: TargetKey[] = ["지자체", "금융사", "브랜드", "POS사"];
    const interval = setInterval(() => {
      setTarget((prev) => {
        const idx = targetKeys.indexOf(prev);
        return targetKeys[(idx + 1) % targetKeys.length];
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const targets: Record<TargetKey, string[]> = {
    지자체: [
      "시민 참여 데이터 확보",
      "탄소감축 실적 관리",
      "인센티브 지급 연계",
    ],
    금융사: [
      "앱 내 ESG 서비스 확장",
      "카드사 구분 없는 영수증 확인",
      "친환경 소비 데이터 연계",
    ],
    브랜드: [
      "종이영수증 비용 절감",
      "쿠폰·스탬프·리뷰 연계",
      "고객 접점 디지털 전환",
    ],
    POS사: ["기존 결제 인프라 활용", "가맹점 확산 용이", "신규 수익모델 확보"],
  };

  const client = [
    {
      partner: "폴바셋",
      imageURL: "/assets/images/img/receipt/paulbassett.jpg",
    },
    { partner: "국민은행", imageURL: "/assets/images/img/receipt/kb.jpg" },
    { partner: "삼성전자", imageURL: "/assets/images/img/receipt/samsung.jpg" },
    { partner: "피자헛", imageURL: "/assets/images/img/receipt/pizzahut.jpg" },
    {
      partner: "탐엔탐스",
      imageURL: "/assets/images/img/receipt/tomntoms.jpg",
    },
    {
      partner: "롯데호텔",
      imageURL: "/assets/images/img/receipt/lottehotel.jpg",
    },
    { partner: "YES24", imageURL: "/assets/images/img/receipt/yes24.jpg" },
    {
      partner: "순천향대학교\n중앙의료원",
      imageURL: "/assets/images/main_univesity_logo.jpg",
    },
    { partner: "KT", imageURL: "/assets/images/main_kt.png" },
    { partner: "LG U+", imageURL: "/assets/images/main_lgu.png" },
    { partner: "NICE\n정보통신", imageURL: "/assets/images/main_nice.png" },
    { partner: "OKPOS", imageURL: "/assets/images/main_okpos.jpg" },
    { partner: "POS Master", imageURL: "/assets/images/main_posmaster.png" },
    { partner: "메타시티", imageURL: "/assets/images/main_metacity.png" },
    { partner: "우주피엔씨", imageURL: "/assets/images/main_pnc.png" },
    { partner: "유노비전", imageURL: "/assets/images/main.uno.png" },
  ];

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const timelineVariants: Variants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  const historyPeriods = [
    {
      period: "2025~2021년",
      events: [
        {
          year: "2025",
          text: "오산시 전자영수증 시범사업 전국 최초 시행\n순천향병원 ESG 솔루션 도입 사업 계약 체결",
          highlight: "ESG",
          color: "bg-emerald-800",
        },
        {
          year: "2024",
          text: "한국인터넷진흥원 블록체인 API 육성 사업자 선정\n롯데호텔 서비스 런칭\n제주도 탄소감축사업 MOU 체결\nSBA 주관 '약자를 위한 기술개발 지원사업' 선정",
          highlight: "확장",
          color: "bg-emerald-700",
        },
        {
          year: "2023",
          text: "삼성전자, 피자헛, 탐앤탐스, 롯데호텔 등 전자영수증 런칭",
          highlight: "런칭",
          color: "bg-emerald-600",
        },
        {
          year: "2023",
          text: "국민은행 스타뱅킹 앱 내에 전자영수증 서비스 런칭",
          highlight: "제휴",
          color: "bg-emerald-600",
        },
        {
          year: "2022",
          text: "폴바셋, 카카오 플러스홈 전자영수증 런칭",
          highlight: "서비스",
          color: "bg-emerald-500",
        },
        {
          year: "2020 ~ 22",
          text: "NH멤버스 팜스토리 사이트 운영 및 유지보수",
          highlight: "운영",
          color: "bg-emerald-500",
        },
        {
          year: "2021",
          text: "KT, TOSS 전자영수증 계약 체결",
          highlight: "계약",
          color: "bg-emerald-400",
        },
        {
          year: "2021",
          text: "스마트 주차 앱 런칭",
          highlight: "앱",
          color: "bg-emerald-400",
        },
      ],
    },
    {
      period: "2020~2018년",
      events: [
        {
          year: "2020",
          text: "중소벤처기업부 AI&빅데이터(결제) R&D에 2년간 선정\n서울산업진흥원(SBA) 스마트 주차 APP R&D에 선정\n중소기업벤처부 장관상, 중소기업중앙회상 수상",
          highlight: "R&D",
          color: "bg-emerald-900",
        },
        {
          year: "2019 ~ 20",
          text: "특허청 IP사업에 선정(특허 15종 추가 출원), 지적재산권 글로벌 연구 및 선점",
          highlight: "특허",
          color: "bg-emerald-900",
        },
        {
          year: "2019",
          text: '전자영수증 관련 부가가치세법 개정 제안 및 국회 토론회\n환경공단(환경부)에 전자영수증 "탄소포인트" 제도 제안 및 연구 내용 제출',
          highlight: "정책",
          color: "bg-emerald-900",
        },
        {
          year: "2019",
          text: "SKT 빌레터 어플리케이션 설계 및 시스템 개발",
          highlight: "개발",
          color: "bg-emerald-800",
        },
        {
          year: "2019",
          text: "온라인투어 'snsmarket' 쇼핑몰 웹사이트 구축 및 고도화",
          highlight: "구축",
          color: "bg-emerald-800",
        },
        {
          year: "2018",
          text: "LG 유플러스 멤버십 모바일 어플리케이션 설계 및 개발",
          highlight: "개발",
          color: "bg-emerald-700",
        },
      ],
    },
    {
      period: "2017~2011년",
      events: [
        {
          year: "2017",
          text: "가맹점 핀테크 융합 서비스 R&D에 선정",
          highlight: "핀테크",
          color: "bg-emerald-800",
        },
        {
          year: "2015 ~ 16",
          text: "전자영수증 R&D 및 시범사업을 진행",
          highlight: "시범",
          color: "bg-emerald-700",
        },
        {
          year: "2014",
          text: "(주)더리얼 전자영수증 대통령 규제개혁 안건에 상정",
          highlight: "정부",
          color: "bg-emerald-600",
        },
        {
          year: "2014",
          text: "하나카드(하나 SK카드) 웹사이트 설계 및 구축, 운영",
          highlight: "구축",
          color: "bg-emerald-600",
        },
        {
          year: "2013",
          text: "국내 최초 전자영수증 발급 방법 특허 등록\n미래창조과학부 전자영수증 제안 및 NIPA(정보통신 산업진흥원) 연구보고서 제출",
          highlight: "최초",
          color: "bg-emerald-500",
        },
        {
          year: "2013",
          text: "IBK 기업은행 카드몰 웹사이트 구축 및 고도화",
          highlight: "구축",
          color: "bg-emerald-500",
        },
        {
          year: "2011",
          text: "㈜더리얼 마케팅 설립",
          highlight: "설립",
          color: "bg-emerald-400",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f7faf7] text-slate-900 font-sans">
      {/* Hero */}
      <section
        id="overview"
        className="relative overflow-hidden bg-white pt-32"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(16,185,129,0.13),transparent_32%),radial-gradient(circle_at_80%_10%,rgba(132,204,22,0.14),transparent_28%)]" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid laptop:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl tablet:text-4xl laptop:text-[2.25rem] desktop:text-[2.5rem] ultra:text-[2.75rem] font-black leading-[1.2] tracking-tight text-slate-950">
              <span className="block laptop:whitespace-nowrap">
                결제와 동시에{" "}
                <span className="text-emerald-700">자동 발급되는</span>
              </span>
              <span className="block laptop:whitespace-nowrap mt-4 tablet:mt-5 laptop:mt-6">
                탄소감축 전자영수증
              </span>
            </h1>
            <p className="mt-7 text-base laptop:text-[17px] desktop:text-lg leading-8 text-slate-600 max-w-xl [text-wrap:pretty]">
              별도 앱 설치, 바코드 스캔, 키인 없이 기존 POS·VAN 연동만으로
              <br className="hidden laptop:inline" />
              전자영수증 발급부터 탄소감축 데이터, 인센티브, 정책 통계까지
              연결합니다.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button
                onClick={didShowInquirePage}
                className="rounded-2xl bg-emerald-700 px-6 py-4 text-white font-bold shadow-lg shadow-emerald-800/15 flex items-center gap-2 hover:bg-emerald-800 transition"
              >
                서비스 도입 문의 <ArrowRight size={18} />
              </button>
              <button
                onClick={didShowIntroducePage}
                className="rounded-2xl bg-white border border-slate-200 px-6 py-4 text-slate-800 font-bold hover:border-emerald-500 transition"
              >
                회사 소개 보기
              </button>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-8 bg-emerald-100 rounded-[48px] rotate-3" />
            <div className="relative">
              <PhoneMockup />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionLabel number="02" title="문제 제기 섹션" />
        <div className="grid tablet:grid-cols-2 laptop:grid-cols-4 gap-4">
          {[
            {
              title: "종이영수증 발급·보관·폐기 부담",
              desc: "매장은 용지·잉크 비용과 폐기 부담, 소비자는 보관 불편을 동시에 겪고 있습니다.",
            },
            {
              title: "브랜드별 앱 설치 방식의 한계",
              desc: "매장마다 별도 앱을 설치해야 해 참여율이 낮고 일관된 사용자 경험을 만들기 어렵습니다.",
            },
            {
              title: "사진 촬영·수동 인증의 참여 허들",
              desc: "영수증 촬영과 수기 입력 같은 번거로운 인증 과정이 친환경 활동의 진입 장벽이 됩니다.",
            },
            {
              title: "증빙 자료 위변조 및 행정 검증의 어려움",
              desc: "실물 증빙은 위변조 여부를 실시간으로 파악하기 어렵고, 사후 검증 과정에서 막대한 행정력과 비용이 소모됩니다.",
            },
          ].map((item, i) => (
            <div
              key={item.title}
              className="group rounded-3xl bg-white border border-slate-100 p-6 shadow-sm hover:shadow-xl hover:shadow-emerald-200/40 hover:border-emerald-400 hover:-translate-y-1 transition-all duration-300 cursor-default"
            >
              <div className="text-4xl font-black text-emerald-500 mb-4 group-hover:text-emerald-700 group-hover:scale-110 origin-left transition-all duration-300">
                0{i + 1}
              </div>
              <h3 className="font-bold text-slate-900 leading-6 group-hover:text-emerald-800 transition-colors duration-300">
                {item.title}
              </h3>
              <p className="mt-4 text-sm text-slate-500 leading-6 [text-wrap:pretty]">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <SectionLabel number="03" title="주요 강점 섹션" />
          <div className="flex flex-col tablet:flex-row tablet:items-end tablet:justify-between gap-6 tablet:gap-8 mb-8">
            <div>
              <h2 className="text-3xl laptop:text-4xl font-black tracking-tight">
                전자영수증 발급부터
                <br />
                정책 연계까지 한 번에
              </h2>
            </div>
            <p className="hidden tablet:block text-slate-500 leading-7 max-w-md">
              결제 인프라 연동, 자동 발급, 데이터 분석, 인센티브 연계까지 4개
              핵심 축으로 전자영수증의 가치를 제공합니다.
            </p>
          </div>
          <div className="grid tablet:grid-cols-2 laptop:grid-cols-4 gap-5">
            <FeatureCard
              icon={Zap}
              title="자동 발급"
              desc="소비자가 별도 조작을 하지 않아도 결제와 동시에 전자영수증이 자동 발급됩니다."
            />
            <FeatureCard
              icon={PlugZap}
              title="POS·VAN 연동"
              desc="기존 결제 인프라와 연동하여 별도 기기 설치 부담을 최소화합니다."
            />
            <FeatureCard
              icon={TrendingDown}
              title="탄소감축 데이터"
              desc="종이영수증 미발급과 친환경 소비 활동을 데이터로 전환합니다."
            />
            <FeatureCard
              icon={Coins}
              title="인센티브 연계"
              desc="탄소중립포인트, 지역화폐, ESG 금융 서비스와 연계할 수 있습니다."
            />
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionLabel number="04" title="작동 구조 섹션" />
        <div className="rounded-[36px] bg-emerald-800 p-8 laptop:p-10 text-white overflow-hidden relative">
          <div className="absolute right-0 top-0 w-80 h-80 bg-white/10 rounded-full translate-x-1/2 -translate-y-1/2" />
          <h2 className="text-2xl laptop:text-3xl font-black mb-8 relative">
            결제 데이터가 전자영수증과 탄소감축 데이터로 연결됩니다.
          </h2>
          <div className="grid tablet:grid-cols-2 laptop:grid-cols-5 gap-4 laptop:gap-10 relative">
            {[
              "소비자 결제",
              "POS/VAN",
              "더리얼 시스템",
              "전자영수증",
              "통계/포인트",
            ].map((step, i) => (
              <div
                key={step}
                className="group relative rounded-3xl bg-white/10 border border-white/15 p-5 min-h-[120px] hover:bg-white/20 hover:border-white/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-950/30 transition-all duration-300 cursor-default"
              >
                <div className="text-sm text-emerald-100 font-bold group-hover:text-white transition-colors duration-300">
                  STEP {i + 1}
                </div>
                <div className="mt-4 text-lg font-black group-hover:scale-[1.03] origin-left transition-transform duration-300">
                  {step}
                </div>
                {i < 4 && (
                  <ChevronRight
                    size={20}
                    className="hidden laptop:block absolute -right-[30px] top-1/2 -translate-y-1/2 text-white/80 group-hover:text-white group-hover:translate-x-1 transition-all duration-300"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions (rich feature cards) */}
      <section id="solutions" className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-16 laptop:py-20">
          <SectionLabel number="05" title="핵심 기능 섹션" />
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <h2 className="text-3xl laptop:text-4xl font-black tracking-tight text-slate-900 leading-[1.25]">
              더리얼만의 <span className="text-emerald-700">차별화된</span>
              <br className="tablet:hidden" /> 전자영수증 기술
            </h2>
            <p className="mt-4 text-slate-500 leading-7">
              마케팅·환경·데이터 분석까지 전자영수증을 핵심 인프라로 한 통합
              플랫폼을 제공합니다.
            </p>
          </div>
          <div className="space-y-16 laptop:space-y-24">
            {[
              {
                badge: "MARKETING",
                title: "영수증 기반\n통합 마케팅 플랫폼",
                image: "/assets/images/main_card_receipt.png",
                desc: "단순한 종이 대체를 넘어 고객과 실시간으로 소통하는 양방향 마케팅 채널. 별도 앱 없이 카카오톡으로 즉시 도달.",
                stats: [
                  "네이버 리뷰 자동 등록",
                  "리뷰 시 쿠폰 발급",
                  "스탬프 적립 자동화",
                  "주차권 자동 발급",
                ],
              },
              {
                badge: "ENVIRONMENT",
                title: "탄소중립포인트\n연계 시스템",
                image: "/assets/images/main_point_receipt.png",
                desc: "환경부 인증 탄소중립 실천 포인트 제도와 완벽 연동. 소비자는 혜택을, 기업은 ESG 성과를 동시에 달성.",
                stats: [
                  "발급 건당 탄소중립포인트 적립",
                  "연간 7만원 혜택",
                  "ESG 실적 자동 집계",
                  "인증서 발급",
                ],
              },
              {
                badge: "DATA ANALYTICS",
                title: "실시간 소비\n데이터 분석 플랫폼",
                image: "/assets/images/main_ai_receipt.png",
                desc: "SKU 단위의 상세 구매 데이터로 정확한 시장 분석과 마케팅 전략 수립. 실시간 매출 현황과 고객 행동 패턴 파악.",
                stats: [
                  "품목별 판매 현황",
                  "매출 분석",
                  "재방문율 추적",
                  "경쟁사 대비 분석",
                ],
              },
            ].map((f, idx) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className={`flex flex-col-reverse tablet:flex-row ${
                  idx % 2 === 1 ? "tablet:flex-row-reverse" : ""
                } gap-8 laptop:gap-20 items-center`}
              >
                <div className="w-full tablet:w-1/2 relative group">
                  <div className="absolute -inset-3 bg-emerald-100/60 rounded-[40px] rotate-2 group-hover:rotate-3 transition-transform duration-500" />
                  <div
                    className="relative w-full h-64 laptop:h-80 rounded-3xl shadow-xl shadow-emerald-200/40 overflow-hidden bg-slate-100 group-hover:scale-[1.02] transition-transform duration-500"
                    style={{
                      backgroundImage: `url(${f.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                </div>
                <div className="w-full tablet:w-1/2">
                  <span className="inline-block px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-md text-xs font-black tracking-[0.18em] mb-5">
                    {f.badge}
                  </span>
                  <h3 className="text-2xl laptop:text-4xl font-black text-slate-900 mb-4 whitespace-pre-line leading-[1.2] tracking-tight">
                    {f.title}
                  </h3>
                  <p className="text-slate-600 leading-7 mb-7">{f.desc}</p>
                  <ul className="space-y-3">
                    {f.stats.map((s) => (
                      <li
                        key={s}
                        className="flex items-center gap-3 text-slate-800 font-medium"
                      >
                        <span className="w-6 h-6 rounded-full bg-emerald-700 text-white flex items-center justify-center shrink-0 shadow-sm shadow-emerald-700/20">
                          <ArrowRight size={12} strokeWidth={3} />
                        </span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Targets */}
      <section id="targets" className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <SectionLabel number="06" title="적용 대상 섹션" />
          <div className="flex flex-wrap gap-3 mb-8">
            {(Object.keys(targets) as TargetKey[]).map((t) => (
              <button key={t} onClick={() => setTarget(t)}>
                <Pill active={target === t}>{t}</Pill>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 laptop:grid-cols-4 gap-3 tablet:gap-5">
            <TargetCard
              icon={Building2}
              title="지자체"
              active={target === "지자체"}
              bullets={targets.지자체}
            />
            <TargetCard
              icon={CreditCard}
              title="금융사"
              active={target === "금융사"}
              bullets={targets.금융사}
            />
            <TargetCard
              icon={Store}
              title="브랜드"
              active={target === "브랜드"}
              bullets={targets.브랜드}
            />
            <TargetCard
              icon={MonitorSmartphone}
              title="POS사"
              active={target === "POS사"}
              bullets={targets.POS사}
            />
          </div>
        </div>
      </section>

      {/* Reference */}
      <section id="reference" className="max-w-7xl mx-auto px-6 py-16">
        <SectionLabel number="07" title="레퍼런스·신뢰 섹션" />
        <div className="grid laptop:grid-cols-3 gap-6">
          <div className="laptop:col-span-2 rounded-[36px] bg-white border border-slate-100 p-8 shadow-sm">
            <h2 className="text-2xl laptop:text-3xl font-black mb-3">
              검증된 기술과 협업 경험
            </h2>
            <p className="text-slate-500 leading-7 mb-8">
              국내 주요 브랜드·금융·공공 기관과 함께한 도입 사례와 특허·정부사업
              선정 이력을 바탕으로 신뢰할 수 있는 전자영수증 인프라를
              제공합니다.
            </p>
            <div className="grid tablet:grid-cols-3 gap-4">
              <FeatureCard
                icon={ShieldCheck}
                title="특허 16종"
                desc="전자영수증 관련 지식재산권 보유로 검증된 기술력을 갖추고 있습니다."
              />
              <FeatureCard
                icon={Building2}
                title="정부사업 선정"
                desc="공공·기술 기반의 다양한 국책사업에 선정된 이력을 보유하고 있습니다."
              />
              <FeatureCard
                icon={Leaf}
                title="탄소감축 연계"
                desc="전자영수증을 탄소중립 정책과 연계해 ESG 가치를 실현합니다."
              />
            </div>
          </div>
          <div
            id="contact"
            className="rounded-[36px] bg-emerald-700 p-8 text-white shadow-xl shadow-emerald-800/15 flex flex-col justify-between min-h-[420px]"
          >
            <div>
              <Mail size={34} />
              <h2 className="mt-6 text-2xl laptop:text-3xl font-black leading-tight">
                전자영수증 도입을 고민하고 계신가요?
              </h2>
              <p className="mt-4 text-emerald-50 leading-7">
                지자체, 금융사, 브랜드, POS/VAN 사업자별 적용 방안을 상담받아
                보세요.
              </p>
            </div>
            <button
              onClick={didShowInquirePage}
              className="mt-10 rounded-2xl bg-white text-emerald-800 px-6 py-4 font-black flex items-center justify-center gap-2 hover:bg-emerald-50 transition"
            >
              서비스 도입 문의 <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section id="partners" className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <SectionLabel number="08" title="협력사 섹션" />
          <div className="text-center mb-12">
            <h2 className="text-3xl laptop:text-4xl font-black tracking-tight text-slate-900 mb-3">
              더리얼 전자영수증 협력사
            </h2>
            <p className="text-slate-500 leading-7 whitespace-pre-line">
              {"신뢰할 수 있는 파트너들과 함께\n더 나은 서비스를 제공합니다"}
            </p>
          </div>
          <div className="relative overflow-hidden">
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10" />

            <div className="flex gap-5 w-max animate-scroll-left [animation-duration:40s] hover:[animation-play-state:paused]">
              {[...client, ...client].map((entity, idx) => (
                <div
                  key={`${entity.partner}-${idx}`}
                  className="shrink-0 w-44 rounded-3xl border border-slate-100 bg-white shadow-sm p-6 flex flex-col items-center justify-center gap-4 min-h-[180px]"
                >
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 flex items-center justify-center p-2">
                    <img
                      src={entity.imageURL}
                      alt={entity.partner}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="text-sm font-bold text-slate-800 whitespace-pre-line text-center leading-5">
                    {entity.partner}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* History (preserved) */}
      <section id="history" className="py-10 laptop:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionLabel number="09" title="연혁 섹션" />
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-slate-900 mb-2">
              더리얼의 연혁
            </h2>
            <p className="mt-4 text-lg text-slate-500 whitespace-pre-line">
              {"혁신적인 전자영수증 서비스로 \n이어온 우리의 여정"}
            </p>
          </div>

          <div className="space-y-12">
            {historyPeriods.map((period, periodIdx) => (
              <motion.div
                key={periodIdx}
                className="bg-gradient-to-br from-emerald-50/40 to-white border border-emerald-100 rounded-2xl shadow-sm overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: periodIdx * 0.1 }}
              >
                <div className="p-4 laptop:p-8 border-b border-emerald-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-1 h-12 bg-gradient-to-b from-emerald-800 to-emerald-500 rounded-full"></div>
                      <div>
                        <h3 className="text-xl laptop:text-2xl font-bold text-slate-900 mb-1">
                          {period.period}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {period.events.length}개의 주요 성과
                        </p>
                      </div>
                    </div>
                    <motion.button
                      onClick={() => togglePeriod(periodIdx)}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-medium transition-colors duration-200 shadow-sm"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="text-sm">
                        {expandedPeriods[periodIdx] ? "접기" : "더보기"}
                      </span>
                      <motion.div
                        animate={{
                          rotate: expandedPeriods[periodIdx] ? 180 : 0,
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </motion.div>
                    </motion.button>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedPeriods[periodIdx] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="p-8 pt-0">
                        <motion.div
                          className="relative"
                          variants={containerVariants}
                          initial="hidden"
                          animate="visible"
                        >
                          <div className="absolute left-6 w-px h-full bg-gradient-to-b from-emerald-300 via-emerald-200 to-transparent laptop:left-1/2 laptop:-translate-x-1/2"></div>

                          <div className="space-y-6 laptop:space-y-8">
                            {period.events.map((event, eventIdx) => (
                              <motion.div
                                key={eventIdx}
                                variants={timelineVariants}
                                className={`flex flex-col laptop:flex-row items-start laptop:items-center gap-6 ${
                                  eventIdx % 2 === 0
                                    ? "laptop:flex-row"
                                    : "laptop:flex-row-reverse"
                                }`}
                              >
                                <div
                                  className={`w-full laptop:w-5/12 ${eventIdx % 2 === 0 ? "" : "laptop:text-right"} ml-16 laptop:ml-0`}
                                >
                                  <motion.div
                                    className="bg-white rounded-xl shadow-md hover:shadow-lg border border-emerald-100 p-6 mr-12 transition-all duration-300"
                                    whileHover={{
                                      scale: 1.01,
                                      y: -2,
                                      boxShadow:
                                        "0 12px 24px -4px rgba(0, 0, 0, 0.08), 0 4px 8px -2px rgba(0, 0, 0, 0.04)",
                                    }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 400,
                                      damping: 25,
                                    }}
                                  >
                                    <div
                                      className={`flex items-center gap-3 mb-4 ${eventIdx % 2 === 0 ? "" : "laptop:justify-end"}`}
                                    >
                                      <span
                                        className={`inline-flex items-center px-3 py-1.5 ${event.color} text-white text-xs font-semibold rounded-md shadow-sm`}
                                      >
                                        {event.highlight}
                                      </span>
                                      <span className="text-xl laptop:text-2xl font-bold text-slate-900 tracking-tight">
                                        {event.year}
                                      </span>
                                    </div>
                                    <div className="prose prose-sm laptop:prose-base max-w-none">
                                      <p className="text-slate-600 leading-relaxed whitespace-pre-line m-0 text-sm laptop:text-[14px]">
                                        {event.text}
                                      </p>
                                    </div>
                                  </motion.div>
                                </div>

                                <div className="hidden laptop:flex w-2/12 justify-center">
                                  <motion.div
                                    className="relative"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                      delay: eventIdx * 0.05,
                                      type: "spring",
                                      stiffness: 600,
                                      damping: 25,
                                    }}
                                  >
                                    <div
                                      className={`w-4 h-4 ${event.color} rounded-full border-3 border-white shadow-md z-10`}
                                    ></div>
                                    <div className="absolute inset-0 w-6 h-6 bg-emerald-100 rounded-full -m-1 -z-10"></div>
                                  </motion.div>
                                </div>

                                <motion.div
                                  className="absolute left-6 transform -translate-x-1/2 laptop:hidden"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{
                                    delay: eventIdx * 0.05,
                                    type: "spring",
                                    stiffness: 600,
                                    damping: 25,
                                  }}
                                >
                                  <div
                                    className={`w-3 h-3 ${event.color} rounded-full border-2 border-white shadow-md z-10`}
                                  ></div>
                                  <div className="absolute inset-0 w-5 h-5 bg-emerald-50 rounded-full -m-1 -z-10"></div>
                                </motion.div>
                                <div className="hidden laptop:block w-5/12"></div>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!expandedPeriods[periodIdx] && (
                  <div className="px-4 laptop:px-8 pb-8">
                    <div className="grid grid-cols-1 laptop:grid-cols-3 gap-4">
                      {period.events.slice(0, 3).map((event, idx) => (
                        <motion.div
                          key={idx}
                          className="bg-emerald-50/50 rounded-lg p-4 border border-emerald-100"
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span
                              className={`w-2 h-2 ${event.color} rounded-full`}
                            ></span>
                            <span className="text-lg font-semibold text-slate-900">
                              {event.year}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2">
                            {event.text.split("\n")[0]}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FooterView />
    </div>
  );
};

export default DigitalReceiptPage;
